import {
  Retailer_Network_User_Role,
  Retailer_Network_Performance_Sort_Order,
  Retailer_Network_Performance_Sort_By,
} from '@occc/graphql';
import { SortingDirection } from '@occc-app/hooks/useSortingParams';
import { getDaysInMonth } from '@occc-app/lib/date';

import { RetailerNetworkConfigurationTabs } from './retailer-connect.types';
import { RETAILER_CONNECT_BASE_URL } from './retailer-connect.constants';

export const routeCreator = {
  base: () => RETAILER_CONNECT_BASE_URL,
  configuration: `${RETAILER_CONNECT_BASE_URL}/configuration`,
  configurationNetworkDetails: `${RETAILER_CONNECT_BASE_URL}/configuration/details`,
  configurationUpdate: (networkId: string) =>
    `${RETAILER_CONNECT_BASE_URL}/${networkId}/configuration`,
  configurationUpdateNetworkDetails: (networkId: string) =>
    `${RETAILER_CONNECT_BASE_URL}/${networkId}/configuration/details`,
  configurationUpdateNetworkLocations: (networkId: string) =>
    `${RETAILER_CONNECT_BASE_URL}/${networkId}/configuration/locations`,
  configurationUpdateNetworkUsers: (networkId: string) =>
    `${RETAILER_CONNECT_BASE_URL}/${networkId}/configuration/users`,
  details: (networkId: string) => `${RETAILER_CONNECT_BASE_URL}/${networkId}`,
  locationDetails: (networkId: string, locationId: string) =>
    `${RETAILER_CONNECT_BASE_URL}/${networkId}/location/${locationId}`,
  locationUpdate: (networkId: string, locationId: string) =>
    `${RETAILER_CONNECT_BASE_URL}/${networkId}/location/${locationId}/update`,
  locationGroupDetails: (networkId: string, locationGroupId: string) =>
    `${RETAILER_CONNECT_BASE_URL}/${networkId}/location-group/${locationGroupId}`,
  userDetails: (networkId: string, userId: string) =>
    `${RETAILER_CONNECT_BASE_URL}/${networkId}/user/${userId}`,
  // New Retailer Connect
  list: () => `${RETAILER_CONNECT_BASE_URL}/list`,
  overview: (networkId: string) => `${RETAILER_CONNECT_BASE_URL}/${networkId}/overview`,
  overviewDefault: () => `${RETAILER_CONNECT_BASE_URL}/overview`,
  performance: (networkId: string) => `${RETAILER_CONNECT_BASE_URL}/${networkId}/performance`,
  performanceDefault: () => `${RETAILER_CONNECT_BASE_URL}/performance`,
  myStores: (networkId: string, showGroups?: boolean) =>
    `${RETAILER_CONNECT_BASE_URL}/${networkId}/my-stores${showGroups ? '?tab=Groups' : ''}`,
  myStoresDefault: () => `${RETAILER_CONNECT_BASE_URL}/my-stores`,
  myStoresGroup: (networkId: string, groupId: string) =>
    `${RETAILER_CONNECT_BASE_URL}/${networkId}/my-stores/group/${groupId}`,
  myStoresStore: (networkId: string, storeId: string) =>
    `${RETAILER_CONNECT_BASE_URL}/${networkId}/my-stores/store/${storeId}`,
  tobacco: (networkId: string) => `${RETAILER_CONNECT_BASE_URL}/${networkId}/tobacco`,
  tobaccoDefault: () => `${RETAILER_CONNECT_BASE_URL}/tobacco`,
  tobaccoScanData: (networkId: string) =>
    `${RETAILER_CONNECT_BASE_URL}/${networkId}/tobacco/scan-data`,
  tobaccoPerformanceDetails: (
    networkId: string,
    searchParams?: tobaccoPerformanceDetailsSearchParams,
  ) => {
    const query = new URLSearchParams(Object.entries(searchParams ?? {})).toString();
    return `${RETAILER_CONNECT_BASE_URL}/${networkId}/tobacco/performance-details${
      query ? `?${query}` : ''
    }`;
  },
};

export type tobaccoPerformanceDetailsSearchParams = {
  metricKey?: string;
  startDate?: string;
  endDate?: string;
};

export const getTabValue = (pathname: string) => {
  const tabValues = Object.values(RetailerNetworkConfigurationTabs);
  const values = tabValues.filter((value) => pathname.endsWith(value));

  if (values.length > 0) {
    return values[0];
  }

  return RetailerNetworkConfigurationTabs.Details;
};

export const getTabItems = (isCreateFlow: boolean) => [
  {
    value: RetailerNetworkConfigurationTabs.Details,
    label: 'Network Details',
    disabled: false,
  },
  {
    value: RetailerNetworkConfigurationTabs.Locations,
    label: 'Network Locations',
    disabled: isCreateFlow,
  },
  {
    value: RetailerNetworkConfigurationTabs.Users,
    label: 'Network Users',
    disabled: isCreateFlow,
  },
];

export const mapRetailerNetworkUserRoleToText: Record<Retailer_Network_User_Role, string> = {
  [Retailer_Network_User_Role.RetailerNetworkAdmin]: 'Network Admin',
  [Retailer_Network_User_Role.RetailerNetworkUser]: 'Network User',
  [Retailer_Network_User_Role.SiteAdmin]: 'Site Admin',
  [Retailer_Network_User_Role.SiteUser]: 'Site User',
};

export const mapSortDirectionRetailerNetworkPerformanceSortOrder: Record<
  SortingDirection,
  Retailer_Network_Performance_Sort_Order
> = {
  asc: Retailer_Network_Performance_Sort_Order.Asc,
  desc: Retailer_Network_Performance_Sort_Order.Desc,
};

export const mapSortOrderToRetailerNetworkPerformanceSortDirection: Record<
  Retailer_Network_Performance_Sort_Order,
  SortingDirection
> = {
  [Retailer_Network_Performance_Sort_Order.Asc]: 'asc',
  [Retailer_Network_Performance_Sort_Order.Desc]: 'desc',
};

export const getRetailerNetworkPerformanceSortBy = (
  fieldName?: string,
): Retailer_Network_Performance_Sort_By => {
  if (fieldName === Retailer_Network_Performance_Sort_By.TotalTransactions) {
    return Retailer_Network_Performance_Sort_By.TotalTransactions;
  }

  return Retailer_Network_Performance_Sort_By.TotalNewMembers;
};

export const isPerformanceOnPace = (goalProgress: number, goalTarget: number, date?: Date) => {
  const forDate = date || new Date();
  const estimatedProgressForDate = (goalTarget * forDate.getDate()) / getDaysInMonth(forDate);

  return goalProgress >= estimatedProgressForDate;
};
